import { appData, queryArgs, storage } from '../utils';
import { isProduction } from './env';

export default (constants) => {
	/**
	 * Update app url to switch user to correct account and redirect to destination url.
	 *
	 * @since 0.1.19
	 *
	 * @param   {string} url       The App destination URL.
	 * @param   {mixed}  accountId The App account to switch to.
	 *
	 * @returns {string}           The updated App URL.
	 */
	const addAppUrlAccountId = (url, accountId = null) => {
		if (accountId) {
			// If on WP and we have an account id, let's make sure they get
			// switched to the right account when they go to the app.
			let appUrl = new URL(app());
			appUrl.searchParams.set('redirect_to', encodeURIComponent(url));
			appUrl.searchParams.set('accountId', accountId);
			url = appUrl.toString();
		}

		return url;
	};

	/**
	 * Adds return arg to bring them back to current page.
	 *
	 * @since 0.1.19
	 *
	 * @param   {string} url       The URL to append the return arg to.
	 * @param   {string} returnUrl Optional return URL to add as query arg. Sets it automatically.
	 *
	 * @returns {string}           URL with return query arg appended.
	 */
	const addReturnUrl = (url, returnUrl) => {
		url = new URL(url);

		url.searchParams.set('return', encodeURIComponent(returnUrl || window.location.href));

		return url.toString();
	};

	/**
	 * Get the App URL with optional path.
	 *
	 * @since 5.13.13
	 *
	 * @param   {string} path App URL path.
	 * @returns {string}      The App URL.
	 */
	const app = (path = '') => {
		let url = appData.get('omAppUrl', constants.APP_URL || 'https://app.optinmonster.com');

		if (path) {
			if ('campaigns/' === path && !isProduction()) {
				return '/campaigns';
			}

			url += '/' + path;
		}

		return url;
	};

	/**
	 * Get the App URL with optional path.
	 *
	 * @since 5.13.47
	 *
	 * @param   {string} path App URL path.
	 * @returns {string}      The App URL.
	 */
	const marketing = (path = '') => {
		let url = appData.get(
			'marketing',
			storage.local.getItem('marketingUrl') || constants.MARKETING_URL || 'https://optinmonster.com'
		);

		if (path) {
			url += '/' + path;
		}

		return url;
	};

	/**
	 * Get the Shareable URL with optional path.
	 *
	 * @since 5.13.47
	 *
	 * @param   {string} path App URL path.
	 * @returns {string}      The App URL.
	 */
	const shareable = (path = '') => {
		if (!isProduction()) {
			return app(path);
		}

		let url = appData.get(
			'shareableUrl',
			storage.local.getItem('shareableUrl') || constants.SHAREABLE_URL || 'https://app.monstercampaigns.com'
		);

		return `${url}/${path}`;
	};

	/**
	 * Get the API URL with optional path.
	 *
	 * @since 5.13.13
	 *
	 * @param   {string} path API URL path.
	 * @param   {string} version API version. Defaults to 'v2'.
	 * @returns {string}      The API URL.
	 */
	const api = (path = '', version = 'v2') => {
		let url = appData.get('omAppApiUrl', constants.API_URL || app());
		url += `/${version}`;

		if (path) {
			url += '/' + path;
		}

		return url;
	};

	/**
	 * Get the TrustPulse API URL with optional path.
	 *
	 * @since 5.13.43
	 *
	 * @param   {string} path API URL path.
	 * @returns {string}      The API URL.
	 */
	const trustpulse = (path = '') => {
		let url = appData.get(
			'tpAppUrl',
			storage.local.getItem('trustpulseUrl') || constants.TP_APP_URL || 'https://app.trustpulse.com'
		);
		url += '/v1';

		if (path) {
			url += '/' + path;
		}

		return url;
	};

	/**
	 * Get the CDN URL with optional path.
	 *
	 * @since 5.13.16
	 *
	 * @param   {string} path API URL path.
	 * @returns {string}      The API URL.
	 */
	const cdn = (path = '') => {
		let url = appData.get('omAppCdnURL', constants.CDN_URL || 'https://a.optinmonster.com');

		if (path) {
			url += '/' + path;
		}

		return url;
	};

	/**
	 * Get the CDN URL for api.js.
	 *
	 * @since 5.20.65
	 *
	 * @returns {string} The CDN URL for api.js.
	 */
	const apiJs = () => {
		return appData.get('apijsUrl', constants.API_JS_URL || cdn('app/js/api.min.js'));
	};

	/**
	 * Get a edit-campaign url.
	 *
	 * @since 5.13.13
	 *
	 * @param   {string}  id        Campaign slug
	 * @param   {string}  path      Whether to show edit/preview/analytics path. Defaults to 'edit/'.
	 * @param   {string}  returnUrl Optional return URL to add as query arg.
	 *
	 * @returns {string}            The edit-campaign url.
	 */
	const campaign = (id, path = 'edit/', returnUrl = '') => {
		const hotnow = appData.get('hotnow', storage.local.getItem('hotnowUrl') || constants.HOTNOW_URL);

		let url = hotnow && ('edit' === path || 0 === path.indexOf('edit/')) ? hotnow : app();

		url += '/campaigns/' + id + '/';
		url += path;

		if (returnUrl) {
			url = addReturnUrl(url, returnUrl);
		}

		return url;
	};

	/**
	 * Get the new-campaign url.
	 *
	 * @since 5.18.41
	 *
	 * @param   {Object} params Optional query params.
	 * @returns {string}        The new-campaign url.
	 */
	const newCampaign = (params = {}) => {
		let url = appData.get('newCampaignUrl', storage.local.getItem('newCampaignUrl') || app() + '/campaigns/new/');

		url = new URL(url);
		if (Object.keys(params).length) {
			let p;
			for (p in params) {
				url.searchParams.set(p, params[p]);
			}
		}

		return url.toString();
	};

	/**
	 * Get the WP site URL with optional path.
	 *
	 * @since 5.20.63
	 *
	 * @param   {string} path Site URL path.
	 * @returns {string}      The site URL.
	 */
	const wp = (path = '') => {
		let url = appData.get('wpUrl', storage.local.getItem('wpUrl') || constants.WP_DEV_URL);

		if (path) {
			url += path;
		}

		return url;
	};

	/**
	 * Get the WP REST API URL with optional path.
	 *
	 * @todo This may have issues if the WP site is in a subdirectory,
	 * so we should pass in the site_url() instead
	 *
	 * @since 5.X.X
	 *
	 * @param   {string} path API URL path.
	 * @returns {string}      The API URL.
	 */
	const wpRest = (path = '') => {
		let url = appData.get(
			'restUrl',
			storage.local.getItem('wpRestUrl') || constants.WP_REST_API_URL || wp() + 'wp-json/'
		);

		if (path) {
			const parts = path.split('?');
			url += parts[0];
			// Let's parse the query args, and add them properly
			// (in case the rest_url already has query args)
			if (parts[1]) {
				const args = queryArgs.parse(parts[1]);
				url = new URL(url);
				Object.keys(args).forEach((k) => {
					url.searchParams.set(k, args[k]);
				});
				url = url.toString();
			}
		}

		return url;
	};

	/**
	 * Get the WP Admin URL with optional query params.
	 *
	 * @todo This may have issues if the WP site is in a subdirectory,
	 * so we should pass in the site_url() instead
	 *
	 * @since 5.20.63
	 *
	 * @param   {string} params Admin URL query params.
	 * @returns {string}      The Admin URL.
	 */
	const wpAdmin = (params = '') => {
		let url = appData.get(
			'adminUrl',
			storage.local.getItem('wpAdminUrl') || constants.WP_ADMIN_API_URL || wp() + 'wp-admin/admin.php'
		);

		if (params) {
			url += params;
		}

		return url;
	};

	return {
		addAppUrlAccountId,
		addReturnUrl,
		app,
		marketing,
		shareable,
		api,
		trustpulse,
		cdn,
		apiJs,
		campaign,
		newCampaign,
		wp,
		wpRest,
		wpAdmin,
	};
};
